import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    IconButton,
    Typography,
    FormControlLabel,
    Switch,
    Box,
    RadioGroup,
    Radio,
    FormLabel,
    FormControl,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import api from '../../api/api';
import en_ja from '../../translations/en_ja';

const AddCoupon = ({
    dialogOpen,
    onClose,
    editMode = false,
    onSubmit,
    setSnackbarType,
    setSnackbarOpen,
    setSnackbarMessage,
    couponData
}) => {
    const userDetails = JSON.parse(localStorage.getItem("user"));

    const [formData, setFormData] = useState({
        coupon_name: '',
        coupon_code: '',
        description: '',
        amount: '',
        minimum_transaction_amount: '',
        from_date: null,
        to_date: null,
        isEnabled: true,
        created_by: userDetails?.id,
        type: "",
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (editMode && couponData) {
            setFormData({
                coupon_name: couponData.coupon_name || '',
                coupon_code: couponData.coupon_code || '',
                description: couponData.description || '',
                amount: couponData.amount || '',
                minimum_transaction_amount: couponData.minimum_transaction_amount || '',
                from_date: couponData.from_date ? dayjs(couponData.from_date) : null,
                to_date: couponData.to_date ? dayjs(couponData.to_date) : null,
                isEnabled: couponData.isEnabled ?? true,
                created_by: couponData.created_by || userDetails?.id
            });
        }
    }, [editMode, couponData]);

    const numberFields = ["amount", "minimum_transaction_amount"];

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (numberFields.includes(name) && value !== "" && Number(value) < 0) {
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        setErrors(prev => ({
            ...prev,
            [name]: ""
        }));
    };

    // block typing of "-", "+", "e", "E" in numeric fields
    const blockNegativeKeys = (e) => {
        if (["-", "+", "e", "E"].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleDateChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleStatusToggle = () => {
        setFormData(prev => ({ ...prev, isEnabled: !prev.isEnabled }));
    };

    const validateForm = () => {
        const newErrors = {};

        // const nameRegex = /^[\p{L}\p{N}\s]+$/u;
        // const descriptionRegex = /^[\p{L}\p{N}\p{P}\s]+$/u;

        if (!formData.type)
            newErrors.type = en_ja["Please select coupon type"];

        if (!formData.coupon_name.trim()) {
            newErrors.coupon_name = en_ja["Coupon name is required"];
        }
        // else if (!nameRegex.test(formData.coupon_name)) {
        //     newErrors.coupon_name =
        //         en_ja["Special characters are not allowed"];
        // }

        if (!formData.coupon_code.trim()) {
            newErrors.coupon_code = en_ja["Coupon code is required"];
        }

        // if (!formData.description.trim()) {
        //     newErrors.description = en_ja["Description is required"];
        // }
        // else if (!descriptionRegex.test(formData.description)) {
        //     newErrors.description =
        //         en_ja["Only letters are allowed"];
        // }

        if (!formData.amount)
            newErrors.amount = en_ja["Discount value is required"];

        if (!formData.minimum_transaction_amount)
            newErrors.minimum_transaction_amount =
                en_ja["Minimum order value is required"];

        if (!formData.from_date)
            newErrors.from_date = en_ja["Start date is required"];

        if (!formData.to_date)
            newErrors.to_date = en_ja["End date is required"];

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleClose = () => {
        setFormData({
            coupon_name: '',
            coupon_code: '',
            description: '',
            amount: '',
            minimum_transaction_amount: '',
            from_date: null,
            to_date: null,
            isEnabled: true,
            created_by: userDetails?.id
        });
        setErrors({});
        onClose();
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setLoading(true);

        try {
            const payload = {
                ...formData,
                from_date: formData.from_date?.format("YYYY/MM/DD"),
                to_date: formData.to_date?.format("YYYY/MM/DD")
            };

            let response;
            if (editMode) {
                response = await api.put(`/v1/admin/coupons/edit/${couponData.id}`, payload);
            } else {
                response = await api.post("/v1/admin/add/coupon", payload);
            }

            if (response.status === 200 || response.status === 201) {
                setSnackbarMessage(response?.data?.message || (editMode ? en_ja["Coupon updated successfully"] : en_ja["Coupon created successfully"]));
                setSnackbarOpen(true);
                setSnackbarType("success");
                if (onSubmit) onSubmit();
                handleClose();
            }
        } catch (error) {
            setSnackbarMessage(error?.response?.data?.error || error?.response?.data?.message || en_ja["Something went wrong"]);
            setSnackbarOpen(true);
            setSnackbarType("error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Dialog open={dialogOpen} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">{editMode ? en_ja["Edit Coupon"] : en_ja["Add New Coupon"]}</Typography>
                        <IconButton onClick={handleClose}><CloseIcon /></IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>

                    <FormControl sx={{ mt: 2 }}>
                        <FormLabel>{en_ja["Select Coupon Type"]}</FormLabel>
                        <RadioGroup
                            row
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                        >
                            <FormControlLabel
                                value="product"
                                control={<Radio />}
                                label={en_ja["Product"]}
                            />
                            <FormControlLabel
                                value="prescription"
                                control={<Radio />}
                                label={en_ja["Prescription"]}
                            />
                        </RadioGroup>
                        {errors.type && (
                            <Typography color="error" variant="caption">
                                {errors.type}
                            </Typography>)}

                    </FormControl>

                    <TextField
                        label={en_ja["Coupon Name"]}
                        name="coupon_name"
                        fullWidth
                        value={formData.coupon_name}
                        onChange={handleChange}
                        margin="normal"
                        error={!!errors.coupon_name}
                        helperText={errors.coupon_name}
                    />

                    <TextField
                        label={en_ja["Coupon Code"]}
                        name="coupon_code"
                        fullWidth
                        value={formData.coupon_code}
                        onChange={handleChange}
                        margin="normal"
                        error={!!errors.coupon_code}
                        helperText={errors.coupon_code}
                    />

                    <TextField
                        label={en_ja["Description"]}
                        name="description"
                        fullWidth
                        multiline
                        minRows={3}
                        value={formData.description}
                        onChange={handleChange}
                        margin="normal"
                        // error={!!errors.description}
                        // helperText={errors.description}
                    />

                    <Box sx={{ display: "flex", gap: 3 }}>
                        <TextField
                            label={en_ja["Discount Value"]}
                            name="amount"
                            fullWidth
                            value={formData.amount}
                            onChange={handleChange}
                            onKeyDown={blockNegativeKeys}
                            margin="normal"
                            error={!!errors.amount}
                            helperText={errors.amount}
                            type='number'
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label={en_ja["Minimum Order Value"]}
                            name="minimum_transaction_amount"
                            fullWidth
                            value={formData.minimum_transaction_amount}
                            onChange={handleChange}
                            onKeyDown={blockNegativeKeys}
                            margin="normal"
                            error={!!errors.minimum_transaction_amount}
                            helperText={errors.minimum_transaction_amount}
                            type='number'
                            inputProps={{ min: 0 }}
                        />
                    </Box>

                    <Box sx={{ display: "flex", gap: 3, mt: 1 }}>
                        <DatePicker
                            label={en_ja["Start Date"]}
                            value={formData.from_date}
                            onChange={(newValue) => handleDateChange("from_date", newValue)}
                            format="YYYY/MM/DD"
                            minDate={dayjs()}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    margin: "normal",
                                    error: !!errors.from_date,
                                    helperText: errors.from_date
                                }
                            }}
                        />
                        <DatePicker
                            label={en_ja["End Date"]}
                            value={formData.to_date}
                            onChange={(newValue) => handleDateChange("to_date", newValue)}
                            format="YYYY/MM/DD"
                            minDate={formData.from_date || dayjs()}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    margin: "normal",
                                    error: !!errors.to_date,
                                    helperText: errors.to_date
                                }
                            }}
                        />
                    </Box>

                    <FormControlLabel
                        label={<Typography variant="subtitle2" fontWeight="bold">{en_ja["Active Status"]}</Typography>}
                        control={<Switch checked={formData.isEnabled} onChange={handleStatusToggle} color="success" />}
                        sx={{ mt: 1 }}
                    />

                </DialogContent>

                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleClose} variant="outlined">
                        {en_ja["Cancel"]}
                    </Button>
                    <LoadingButton onClick={handleSubmit} loading={loading} variant="contained" disabled={loading} sx={{ backgroundColor: "rgba(220, 53, 69, 1)" }}>
                        {editMode ? en_ja["Update Coupon"] : en_ja["Create Coupon"]}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default AddCoupon;
